'use client'
import { useTranslate } from '@repo/shared/hooks'
import {
  Button,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Row,
  Typography,
  Skeleton,
  Separator,
  toast,
} from '@repo/ui/components'
import { OrderBoxAssetSelect } from './order-box.asset-select'
import { OrderBoxAmountInput } from './order-box.amount-input'
import { BalanceSlider } from '../balance-slider'
import type { OrderBoxConvertFormProps, FlatAsset } from './order-box.types'
import { useState, useEffect, useRef } from 'react'
import { useAppForm } from '@repo/ui/hooks'
import { z } from 'zod'
import { InformationDialog, ValueWithUnit, formatValueWithScale } from '..'
import { redirect } from 'next/navigation'
import { OrderBoxConvertDialog } from './order-box.convert-dialog'

export const OrderBoxConvertForm = (props: OrderBoxConvertFormProps) => {
  const {
    fromAsset,
    toAsset,
    onFromAssetChange,
    onToAssetChange,
    onSwapAssets,
    fromAmount,
    toAmount,
    onFromAmountChange,
    onToAmountChange,
    fromBalance,
    toBalance,
    conversionRate,
    isConversionRateLoading,
    availableAssets = [],
    allProjects = [],
    listProjectsData,
    onSearchChange,
    onSubmit,
    isLoading = false,
    targetScale = 1,
    isBalanceLoading = false,
    convertOrderId,
    onConvertFinalize,
    convertOrderBaseAmount,
    convertOrderTargetAmount,
    isCalculatingConvertAmount = false,
    onCalculateReceivedAmount,
    onCalculateReceivedAmountForToAmount,
    defaultReferralCode,
    flatOrderData,
    onReferralChange,
    onCheckReferralCode,
    isPriceValid = true,
    onTimeExpired,
    onRefreshPrice,
    onRefreshConvertPrice,
    isMobile = false,
    selectedAsset,
    referal,
  } = props

  const { t } = useTranslate()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const maxFromAmount = fromBalance / targetScale

  const schema = z.object({
    fromAmount: z
      .union([z.string(), z.number()])
      .transform((val) => (typeof val === 'number' ? String(val) : val))
      .optional(),
    toAmount: z
      .union([z.string(), z.number()])
      .transform((val) => (typeof val === 'number' ? String(val) : val))
      .optional(),
  })

  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      fromAmount: fromAmount || undefined,
      toAmount: toAmount || undefined,
    },
  })

  useEffect(() => {
    form.setValue('fromAmount', fromAmount || undefined)
  }, [fromAmount, form])

  // Clear toAsset if it matches fromAsset or if it's MALEK
  useEffect(() => {
    if (
      fromAsset &&
      toAsset &&
      (fromAsset.token === toAsset.token ||
        fromAsset.id === toAsset.id ||
        toAsset.token === 'MALEK' ||
        toAsset.id === 'MALEK')
    ) {
      onToAssetChange?.(undefined as any)
    }
  }, [fromAsset, toAsset, onToAssetChange])

  useEffect(() => {
    if (selectedAsset) {
      onFromAssetChange?.(selectedAsset as any)
      onToAssetChange?.(undefined as any)
    }
  }, [selectedAsset])

  const handleFromAmountChange = (value: number | undefined) => {
    const amount = value || 0
    form.setValue('fromAmount', amount)
    onFromAmountChange?.(amount)

    // Calculate received amount using API
    if (amount > 0 && onCalculateReceivedAmount) {
      onCalculateReceivedAmount(amount)
    }
  }

  const handleToAmountChange = (value: number | undefined) => {
    const amount = value || 0
    form.setValue('toAmount', amount)
    onToAmountChange?.(amount)

    // Calculate fromAmount using API (reverse calculation)
    if (amount > 0 && onCalculateReceivedAmountForToAmount) {
      onCalculateReceivedAmountForToAmount(amount)
    }
  }

  const handleSwap = () => {
    if (fromAsset?.token === 'MALEK') {
      toast.error({
        title: t('order_convert_swap_error'),
      })
      return
    }

    // Store current amounts before swap
    const currentFromAmount = form.watch('fromAmount') || 0
    const currentToAmount = toAmount || 0

    // Swap assets
    onSwapAssets?.()

    // After swap, swap the amounts as well
    // Previous toAmount becomes new fromAmount
    // Previous fromAmount becomes new toAmount
    // The conversion rate will be automatically inverted, so this works correctly
    // Use setTimeout to ensure assets are swapped first and new conversion rate is calculated
    setTimeout(() => {
      // Set the swapped amounts directly without triggering recalculation
      form.setValue('fromAmount', currentToAmount, { shouldValidate: false })
      form.setValue('toAmount', currentFromAmount, { shouldValidate: false })
      // Update parent state without triggering recalculation
      onFromAmountChange(currentToAmount)
    }, 0)
  }

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    if (!fromAsset || !toAsset || !data.fromAmount) return

    // Update parent state with current form values
    onFromAmountChange(Number(data.fromAmount))

    // Call onSubmit without referral code (it will be handled in dialog)
    onSubmit?.(Number(data.fromAmount))
  }

  // Open dialog when order is placed successfully
  useEffect(() => {
    console.log('convertOrderId in form:', convertOrderId)
    if (convertOrderId) {
      console.log('Opening dialog with orderId:', convertOrderId)
      setIsDialogOpen(true)
    } else {
      setIsDialogOpen(false)
    }
  }, [convertOrderId])

  const handleDialogConfirm = (referralCode?: string) => {
    // Call parent's finalize handler with referral code
    // The parent component has access to convertOrderId via state
    onConvertFinalize?.(referralCode)
  }

  // Wrapper for onCheckReferralCode that uses form's fromAmount
  const handleCheckReferralCodeWrapper = (
    referralCode: string,
    fromAmountParam: number,
    onSuccess: (data: { code: string }) => void,
    onError: (error: any) => void,
  ) => {
    if (!onCheckReferralCode) {
      onError(new Error('onCheckReferralCode is not provided'))
      return
    }

    // Get fromAmount from form (current value) - use param if provided, otherwise form value
    const currentFromAmount =
      fromAmountParam || Number(form.watch('fromAmount') || 0)

    if (!fromAsset || !toAsset || currentFromAmount <= 0) {
      onError(
        new Error(
          'Invalid convert parameters: fromAmount must be greater than 0',
        ),
      )
      return
    }

    // Call the parent's handler with the form's fromAmount
    onCheckReferralCode(referralCode, currentFromAmount, onSuccess, onError)
  }

  const fromAssetLabel = t(
    fromAsset?.kind === 'crypto' || fromAsset?.id === 'MALEK'
      ? 'common_token'
      : 'CM2',
  )

  const toAssetLabel = t(
    toAsset?.kind === 'crypto' || toAsset?.id === 'MALEK'
      ? 'common_token'
      : 'CM2',
  )

  // Filter listProjectsData to exclude fromAsset from toAsset list and exclude MALEK
  const filteredListProjectsData = listProjectsData
    ? {
        ...listProjectsData,
        data: {
          ...listProjectsData.data,
          pages:
            listProjectsData.data?.pages?.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                projects:
                  (page.data?.projects as FlatAsset[])?.filter(
                    (project: FlatAsset) =>
                      project.token !== fromAsset?.token &&
                      project.id !== fromAsset?.id &&
                      project.token !== 'MALEK' &&
                      project.id !== 'MALEK',
                  ) || [],
              },
            })) || [],
        },
      }
    : undefined

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className='flex flex-col justify-between h-full'
      >
        <Col className='gap-md'>
          {/* From Asset Section */}
          <Col className='gap-xs'>
            <Typography size='xs' weight='bold'>
              {t('order_convert_from')}
            </Typography>
            <Row
              className={`gap-0 px-xs py-2xs items-stretch bg-neutral-surface rounded-sm flex-col ${!isMobile ? 'sm:gap-md sm:flex-row' : ''}`}
            >
              <Col className='flex-1 items-center justify-center'>
                <OrderBoxAssetSelect
                  displayBadge={false}
                  selectedAsset={fromAsset}
                  onChange={onFromAssetChange || (() => {})}
                  projects={
                    availableAssets.length > 0 ? availableAssets : allProjects
                  }
                  tab='buy'
                  listProjectsData={listProjectsData}
                  onSearchChange={onSearchChange}
                />
              </Col>
              <Col
                className={`flex-1 items-center justify-center relative min-h-14 border-t border-t-neutral-surface-5 ${!isMobile ? 'sm:border-t-0' : ''}`}
              >
                <Separator
                  className={`hidden absolute top-6 -right-3 !h-xl bg-neutral-surface-20 ${!isMobile ? 'sm:block' : ''}`}
                  orientation='vertical'
                />
                <div className='absolute left-0 top-md w-full h-full'>
                  <FormField
                    control={form.control}
                    name='fromAmount'
                    render={({ field }) => (
                      <FormItemWrapper inputSize='md'>
                        <OrderBoxAmountInput
                          {...field}
                          value={fromAmount}
                          onChange={(value) => {
                            field.onChange(value)
                            if (timeoutRef.current) {
                              clearTimeout(timeoutRef.current as NodeJS.Timeout)
                            }
                            timeoutRef.current = setTimeout(() => {
                              handleFromAmountChange(value)
                            }, 400)
                          }}
                          max={maxFromAmount}
                          targetScale={fromAsset?.scale}
                          tab='sell'
                          assetLabel={fromAssetLabel}
                          placeholder={t('order_convert_from_amount')}
                          noFocusStyle
                          disabled={!fromAsset?.token}
                          scale={fromAsset?.scale}
                        />
                      </FormItemWrapper>
                    )}
                  />
                </div>
              </Col>
            </Row>
          </Col>

          {/* Balance Display and Slider for From Asset */}
          <div className='mt-md'>
            <BalanceSlider
              title={t('order_available_balance_buy')}
              titleProps={{
                size: 'xs',
              }}
              value={
                maxFromAmount > 0
                  ? ((form.watch('fromAmount') || 0) / maxFromAmount) * 100
                  : 0
              }
              onChange={(value) => {
                const amount = (value * maxFromAmount) / 100
                form.setValue('fromAmount', amount)
              }}
              onCommit={(value) => {
                const amount = (value * maxFromAmount) / 100
                handleFromAmountChange(amount)
              }}
              max={100}
              currencyLabel={fromAssetLabel}
              color='neutral'
              availableBalance={(fromBalance / fromAsset?.scale!).toString()}
              disabled={false}
              tab='convert'
              token={fromAsset?.token || ''}
              loading={isBalanceLoading}
              removeSpaceBetweenVertical
              onMaxClick={() => redirect('/wallet/deposit/online-payment/')}
              kind={fromAsset?.kind}
            />
          </div>

          {/* Swap Button */}
          <Row className='justify-center my-xs'>
            <Button
              type='button'
              variant='ghost'
              size='md'
              onClick={handleSwap}
              aria-label={t('order_convert_swap')}
            >
              <Icon name='Swap' size='md' />
            </Button>
          </Row>

          {/* To Asset Section */}
          <Col className='gap-xs'>
            <Typography size='xs' weight='bold'>
              {t('order_convert_to')}
            </Typography>
            <Row
              className={`gap-0 px-xs py-2xs items-stretch bg-neutral-surface rounded-sm flex-col ${!isMobile ? 'sm:gap-md sm:flex-row' : ''}`}
            >
              <Col className='flex-1 items-center justify-center'>
                <OrderBoxAssetSelect
                  displayBadge={false}
                  selectedAsset={toAsset}
                  onChange={onToAssetChange || (() => {})}
                  projects={allProjects.filter(
                    (project) =>
                      project.token !== fromAsset?.token &&
                      project.id !== fromAsset?.id &&
                      project.token !== 'MALEK' &&
                      project.id !== 'MALEK',
                  )}
                  tab='buy'
                  listProjectsData={filteredListProjectsData}
                  onSearchChange={onSearchChange}
                />
              </Col>
              <Col
                className={`flex-1 items-center justify-center relative min-h-14 border-t border-t-neutral-surface-5 ${!isMobile ? 'sm:border-t-0' : ''}`}
              >
                <Separator
                  className={`hidden absolute top-6 -right-3 !h-xl bg-neutral-surface-20 ${!isMobile ? 'sm:block' : ''}`}
                  orientation='vertical'
                />
                <div className='absolute left-0 top-md w-full h-full'>
                  <FormField
                    control={form.control}
                    name='toAmount'
                    render={({ field }) => (
                      <FormItemWrapper
                        // title={t('order_convert_to_amount')}
                        inputSize='md'
                      >
                        <OrderBoxAmountInput
                          {...field}
                          value={toAmount || undefined}
                          onChange={(value) => {
                            field.onChange(value)
                            if (timeoutRef.current) {
                              clearTimeout(timeoutRef.current as NodeJS.Timeout)
                            }
                            timeoutRef.current = setTimeout(() => {
                              handleToAmountChange(Number(value))
                            }, 400)
                          }}
                          max={undefined}
                          targetScale={fromAsset?.scale}
                          tab='buy'
                          assetLabel={toAssetLabel}
                          placeholder={t('order_convert_to_amount')}
                          noFocusStyle
                          disabled={isCalculatingConvertAmount}
                          scale={toAsset?.scale}
                        />
                      </FormItemWrapper>
                    )}
                  />
                </div>
              </Col>
            </Row>
          </Col>

          {/* Conversion Rate Display */}
          <Row className='justify-between items-center mt-md'>
            <Row className='items-center gap-xs w-fit'>
              <Typography
                size='xs'
                color='text-neutral-surface-80'
                weight='regular'
              >
                {t('order_convert_rate')}
              </Typography>
              <InformationDialog title={t('order_convert_rate')}>
                {t('order_convert_rate_info')}
              </InformationDialog>
            </Row>
            {isConversionRateLoading ? (
              <Skeleton className='h-4 w-32' />
            ) : fromAsset && toAsset && conversionRate ? (
              <Row className='items-center gap-xs w-fit flex-row-reverse'>
                <ValueWithUnit
                  value={1}
                  className='ltr'
                  valueSize='sm'
                  valueWeight='bold'
                  betweenChildren={
                    <Typography size='sm' color='text-neutral-surface-60'>
                      {fromAsset.token}
                    </Typography>
                  }
                />
                <Typography size='sm' color='text-neutral-surface-80'>
                  ≈
                </Typography>
                <ValueWithUnit
                  value={conversionRate}
                  className='ltr'
                  valueSize='sm'
                  valueWeight='bold'
                  decimalPlaces={9}
                  betweenChildren={
                    <Typography size='sm' color='text-neutral-surface-60'>
                      {toAsset.token}
                    </Typography>
                  }
                />
              </Row>
            ) : (
              <Typography size='sm' color='text-neutral-main' weight='bold'>
                -
              </Typography>
            )}
          </Row>
        </Col>
        {/* Convert Button */}
        <div className='bg-neutral-surface-light w-full p-md pt-0 sm:p-0 sm:pt-md sm:relative fixed bottom-0 left-0 right-0 w-auto'>
          <Button
            type='submit'
            size='lg'
            variant='fill'
            className='w-full'
            disabled={
              !fromAsset ||
              !toAsset ||
              !form.watch('fromAmount') ||
              form.watch('fromAmount') === 0 ||
              isLoading
            }
            loading={isLoading}
          >
            {t('order_convert')}
          </Button>
        </div>
      </form>

      {/* Convert Dialog */}
      <OrderBoxConvertDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        fromAsset={fromAsset}
        toAsset={toAsset}
        fromAmount={fromAmount || 0}
        toAmount={toAmount || 0}
        conversionRate={conversionRate}
        onConfirm={handleDialogConfirm}
        isLoading={isLoading}
        targetScale={targetScale}
        targetAmount={convertOrderTargetAmount}
        defaultReferralCode={defaultReferralCode}
        flatOrderData={flatOrderData}
        onReferralChange={onReferralChange}
        hasDefaultReferral={!!defaultReferralCode}
        onCheckReferralCode={handleCheckReferralCodeWrapper}
        isPriceValid={isPriceValid}
        onTimeExpired={onTimeExpired}
        onRefreshPrice={onRefreshPrice}
        onRefreshConvertPrice={(
          fromAmountParam,
          referralCodeParam,
          onSuccess,
        ) => {
          // Get fromAmount from form or use provided param
          const currentFromAmount =
            fromAmountParam || Number(form.watch('fromAmount') || 0)
          // Get referral code from state or use provided param
          const currentReferralCode =
            referralCodeParam || defaultReferralCode || ''
          // Call the parent's onRefreshConvertPrice handler
          // This will call the API and then call onSuccess to update isPriceValid
          if (onRefreshConvertPrice) {
            onRefreshConvertPrice(
              currentFromAmount,
              currentReferralCode,
              onSuccess,
            )
          } else {
            // Fallback: call onSubmit
            onSubmit?.(currentFromAmount, currentReferralCode)
            onSuccess?.()
          }
        }}
        referal={referal}
      />
    </Form>
  )
}
